import t from "@/lang/lang";
import {
  PestcontrolMonitor,
  PestcontrolMonitorType,
} from "@/services/backend/pestcontrol/types/monitor";
import {
  Pest,
  PestGroup,
  PestGroupSnapshot,
  PestKind,
  PestSnapshot,
} from "@/services/backend/pestcontrol/types/pest";
import { v4 } from "uuid";

export function newMonitorDraft(companyId: string): PestcontrolMonitor {
  return {
    id: v4(),
    companyId,
    key: "",
    name: "",
    supplier: "",
    type: PestcontrolMonitorType.Checkpoint,
    times: { install: 0, maintain: 0 },
    capabilities: {
      permanentBaiting: false,
      hasLethalAgent: false,
      checkpointVisual: false,
    },
    buyPrice: 0,
    recommendedVisits: 0,
    details: {
      description: "",
      installationType: "",
      heightMm: 0,
      widthMm: 0,
      depthMm: 0,
      weightG: 0,
      coverageValue: 0,
      coveragePreText: "",
      coveragePostText: "",
      starterSpecification: "",
      protectionLevel: "",
    },
    pests: [],
    groups: [],
  };
}

export function normalizeMonitor(
  monitor: PestcontrolMonitor,
): PestcontrolMonitor {
  return {
    ...monitor,
    times: monitor.times ?? { install: 0, maintain: 0 },
    capabilities: monitor.capabilities ?? {
      permanentBaiting: false,
      hasLethalAgent: false,
      checkpointVisual: false,
    },
    details: monitor.details ?? newMonitorDraft(monitor.companyId).details,
    buyPrice: monitor.buyPrice ?? 0,
    pests: Array.isArray(monitor.pests) ? monitor.pests : [],
    groups: Array.isArray(monitor.groups) ? monitor.groups : [],
  };
}

export function normalizeMonitorForSave(
  monitor: PestcontrolMonitor,
): PestcontrolMonitor {
  const normalized = normalizeMonitor(monitor);
  return {
    ...normalized,
    supplier: normalized.supplier.trim(),
    name: normalized.name.trim(),
    details: {
      ...normalized.details,
      description: normalized.details.description.trim(),
      installationType: normalized.details.installationType.trim(),
    },
  };
}

export function pestToSnapshot(pest: Pest): PestSnapshot {
  return {
    companyId: pest.companyId,
    sourceId: pest.id,
    kind: pest.kind,
    group: pest.group,
    name: pest.name,
    technicalTerm: pest.technicalTerm ?? "",
    abbreviation: pest.abbreviation ?? "",
    description: pest.description ?? "",
    synonyms: pest.synonyms ?? [],
    takenAt: new Date().toISOString(),
  };
}

export function groupToSnapshot(group: PestGroup): PestGroupSnapshot {
  return {
    companyId: group.companyId,
    sourceId: group.id,
    name: group.name,
    description: group.description ?? "",
    takenAt: new Date().toISOString(),
  };
}

export function groupsFromPests(pests: PestSnapshot[]): PestGroupSnapshot[] {
  return mergeGroups(
    pests.flatMap((pest) => (pest.group ? [pest.group] : [])),
    [],
  );
}

export function mergeGroups(
  left: PestGroupSnapshot[],
  right: PestGroupSnapshot[],
): PestGroupSnapshot[] {
  const byID = new Map<string, PestGroupSnapshot>();
  for (const group of [...left, ...right]) {
    byID.set(group.sourceId, group);
  }
  return [...byID.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function comparePests(left: Pest, right: Pest): number {
  const kind = left.kind.localeCompare(right.kind);
  if (kind !== 0) return kind;
  return left.name.localeCompare(right.name);
}

export function formatMonitorType(type: PestcontrolMonitorType): string {
  switch (type) {
    case PestcontrolMonitorType.Checkpoint:
      return t("Prüfpunkt");
    case PestcontrolMonitorType.FlyExterminator:
      return t("Fluginsektenvernichter");
    case PestcontrolMonitorType.IoT:
      return t("IoT-Monitor");
    default:
      return type;
  }
}

export function formatDuration(value: number): string {
  if (!value) return "-";
  return `${Math.round(value / 60_000_000_000)} min`;
}

export function formatPrice(value?: number): string {
  if (!value) return "-";
  return `${value.toFixed(2)} €`;
}

export function normalizeKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-")
    .replace(/_+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function isPestKind(value: PestKind | string): value is PestKind {
  return Object.values(PestKind).includes(value as PestKind);
}
